import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import ToastComponent from "./toast-component";
import {
    ESTILO_BOTON_MODAL_ACEPTAR,
    ESTILO_BOTON_MODAL_CANCELAR,
    ESTILO_INPUT_GENERICO,
} from "../utils/const/estilos-const";

const EMPTY_FORM = {
    password: "",
    confirmarPassword: "",
};

export default function ModalResetPassword({
    isOpen = false,
    onCancel,
    onConfirm,
    charLimits = { password: 10, confirmarPassword: 10 },
    title,
    userName = "",
}) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("warning");

    useEffect(() => {
        if (!isOpen) {
            setFormData(EMPTY_FORM);
            setShowPassword(false);
            setShowConfirmPassword(false);
        }
    }, [isOpen]);

    const renderPasswordToggle = (visible, onToggle) => (
        <button
            type="button"
            onClick={onToggle}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
            aria-label={visible ? t("label_ocultar_contrasena") : t("label_mostrar_contrasena")}
        >
            <div className="relative w-5 h-5">
                <Eye
                    className={`absolute inset-0 transition-all duration-300 ${
                        visible ? "opacity-0 scale-75 rotate-90" : "opacity-100 scale-100 rotate-0"
                    }`}
                    size={20}
                />
                <EyeOff
                    className={`absolute inset-0 transition-all duration-300 ${
                        visible ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 -rotate-90"
                    }`}
                    size={20}
                />
            </div>
        </button>
    );

    const showToast = (message, type = "warning", durationMs = 3500) => {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);
        window.clearTimeout(showToast._timer);
        showToast._timer = window.setTimeout(() => {
            setToastVisible(false);
        }, durationMs);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const limit = charLimits[name];
        if (limit == null || value.length <= limit) {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const validarFormulario = () => {
        let message = "";

        if (String(formData.password).trim().length === 0) {
            message = message + t("alert_debe_ingresar_password") + "\n";
        }
        if (String(formData.confirmarPassword).trim().length === 0) {
            message = message + t("alert_debe_ingresar_confirmar_password") + "\n";
        }
        if (
            formData.password !== formData.confirmarPassword &&
            String(formData.password).trim().length > 0 &&
            String(formData.confirmarPassword).trim().length > 0
        ) {
            message =
                message +
                t("alert_la_contraseña_y_la_confirmación_de_la_contraseña_no_coinciden") +
                "\n";
        }

        if (message.length > 0) {
            showToast(message.trim(), "warning", 4000);
            return false;
        }
        return true;
    };

    const handleConfirm = () => {
        if (!validarFormulario()) {
            return;
        }
        onConfirm({
            password: formData.password,
            confirmarPassword: formData.confirmarPassword,
        });
        setFormData(EMPTY_FORM);
    };

    const handleCancel = () => {
        setFormData(EMPTY_FORM);
        onCancel();
    };

    if (!isOpen) {
        return null;
    }

    const modalTitle = title ?? t("column_reset_password");

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={handleCancel}
        >
            <div
                className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-xl font-bold mb-1 text-gray-900">{modalTitle}</h2>
                {userName ? (
                    <p className="text-sm text-gray-500 mb-4">{userName}</p>
                ) : (
                    <div className="mb-4" />
                )}

                <div className="mb-4">
                    <label
                        htmlFor="modal-reset-password"
                        className="block text-gray-700 font-semibold mb-1"
                    >
                        {t("label_password")}
                        <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            id="modal-reset-password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder={t("placeholder_ingresa_password")}
                            className={`${ESTILO_INPUT_GENERICO} pr-12`}
                            maxLength={charLimits.password}
                            autoComplete="new-password"
                        />
                        {renderPasswordToggle(showPassword, () => setShowPassword((prev) => !prev))}
                    </div>
                    <p className="text-sm text-gray-500 flex justify-end">
                        {formData.password.length}/{charLimits.password}
                    </p>
                </div>

                <div className="mb-6">
                    <label
                        htmlFor="modal-reset-confirm-password"
                        className="block text-gray-700 font-semibold mb-1"
                    >
                        {t("label_confirmar_password")}
                        <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            id="modal-reset-confirm-password"
                            name="confirmarPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmarPassword}
                            onChange={handleInputChange}
                            placeholder={t("placeholder_ingresa_password")}
                            className={`${ESTILO_INPUT_GENERICO} pr-12`}
                            maxLength={charLimits.confirmarPassword}
                            autoComplete="new-password"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleConfirm();
                                }
                            }}
                        />
                        {renderPasswordToggle(showConfirmPassword, () =>
                            setShowConfirmPassword((prev) => !prev)
                        )}
                    </div>
                    <p className="text-sm text-gray-500 flex justify-end">
                        {formData.confirmarPassword.length}/{charLimits.confirmarPassword}
                    </p>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className={ESTILO_BOTON_MODAL_CANCELAR}
                    >
                        {t("label_cancelar")}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={ESTILO_BOTON_MODAL_ACEPTAR}
                    >
                        {t("label_aceptar")}
                    </button>
                </div>
            </div>

            <ToastComponent
                visible={toastVisible}
                message={toastMessage}
                type={toastType}
                onClose={() => setToastVisible(false)}
            />
        </div>
    );
}
